/**
 * The home-screen widgets' snapshot (`D280`), handed to the shell's own
 * `Widgets` plugin (`android/app/src/main/java/app/mypocketgym/dev/WidgetsPlugin.java`).
 * The contract is `features/widgets/snapshot.ts`; the shell only stores the
 * string and re-draws.
 *
 * An older shell has no such plugin, and the web has no home screen: both
 * answer {@link widgetsAvailable} `false` and every call is a no-op.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { isNative } from "./platform";

interface WidgetsPlugin {
  clear(): Promise<void>;
  write(options: { snapshot: string }): Promise<void>;
}

const PLUGIN = "Widgets";

let plugin: WidgetsPlugin | null = null;

function widgets(): WidgetsPlugin {
  plugin ??= registerPlugin<WidgetsPlugin>(PLUGIN);
  return plugin;
}

export function widgetsAvailable(): boolean {
  return isNative() && Capacitor.isPluginAvailable(PLUGIN);
}

/** Stores the serialised snapshot and re-draws every placed widget. */
export async function writeWidgetSnapshot(snapshot: string): Promise<void> {
  if (!widgetsAvailable()) {
    return;
  }
  try {
    await widgets().write({ snapshot });
  } catch (error) {
    console.warn("[native] widget snapshot not written", error);
  }
}

/** Forgets the snapshot: the widgets fall back to "open the app". */
export async function clearWidgetSnapshot(): Promise<void> {
  if (!widgetsAvailable()) {
    return;
  }
  try {
    await widgets().clear();
  } catch (error) {
    console.warn("[native] widget snapshot not cleared", error);
  }
}
